import React from "react";
import { Redirect } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useFlag, useFlagsStatus } from "@unleash/proxy-client-react";

import { GET_BEER_BOX_ADDONS } from "../../../graphql/resolvers/quiz";
import { isLoggedIn } from "../../../helpers/auth";
import { UNLEASH_BEER_PACKS } from "../../../helpers/constants";
import { useInitUnleashClient } from "../../../helpers/hooks";
import { BeerBoxAddons } from "../../../types/__generated__/BeerBoxAddons";
import { SubscriptionSubscriptionAddOnStatusChoices } from "../../../types/__generated__/globalTypes";
import URL_PATTERNS from "../../../urls";
import StyledLoading from "../Loading/StyledLoading";

const ACTIVE_BEER_STATUSES = [
  SubscriptionSubscriptionAddOnStatusChoices.ACTIVE,
  SubscriptionSubscriptionAddOnStatusChoices.SKIP,
];

interface BeerSubscriptionClosedRedirectProps {
  children: React.ReactNode;
  allowExistingBeerSubscribers?: boolean;
}

const BeerSubscriptionClosedRedirect = ({
  children,
  allowExistingBeerSubscribers = false,
}: BeerSubscriptionClosedRedirectProps) => {
  // Quiz and subscribe sit outside Layout, which is otherwise the only place
  // that starts the Unleash client. Start it here so a direct visit can read flags.
  const { isInitialized } = useInitUnleashClient();
  const { flagsReady, flagsError } = useFlagsStatus();
  const isBeerPacksEnabled = useFlag(UNLEASH_BEER_PACKS);
  const memberIsLoggedIn = isLoggedIn();
  const shouldCheckExistingBeer =
    allowExistingBeerSubscribers && isBeerPacksEnabled && memberIsLoggedIn && flagsReady;
  const { data, loading: isLoadingExistingBeer } = useQuery<BeerBoxAddons>(GET_BEER_BOX_ADDONS, {
    variables: { category: "beer" },
    skip: !shouldCheckExistingBeer,
  });

  // start() can fail without setting flagsError (CI unleash-stub is often
  // unreachable). isInitialized still flips true in that catch, so we fail open
  // instead of leaving the marketing pages on a spinner.
  if (!flagsReady && !flagsError && !isInitialized) return <StyledLoading />;
  if (!isBeerPacksEnabled) return <>{children}</>;

  if (shouldCheckExistingBeer) {
    if (isLoadingExistingBeer) return <StyledLoading />;
    const hasBeerSubscription = data?.allAddOns?.some((addon) => {
      const status = addon?.memberSubscriptionAddOn?.status;
      return !!status && ACTIVE_BEER_STATUSES.includes(status);
    });
    if (hasBeerSubscription) return <>{children}</>;
  }

  return <Redirect to={URL_PATTERNS.ALL_SPECIAL_PACKS} />;
};

export default BeerSubscriptionClosedRedirect;
